import React, { useState, useEffect } from "react";
import {
  Container,
  Paper,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Box,
  Grid,
  Card,
  CardContent,
  Alert,
  Snackbar,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Tooltip,
} from "@mui/material";
import {
  Add,
  Edit,
  Delete,
  PlayCircleOutline,
  StopCircle,
  Visibility,
  DateRange,
  AccessTime,
  CheckCircle,
  Cancel,
} from "@mui/icons-material";
import { LocalizationProvider, DatePicker } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import axios from "axios";

const API_URL = process.env.REACT_APP_API_URL || "http://localhost:5000";
const api = axios.create({ baseURL: API_URL });

const AppraisalCycleManager = () => {
  const [cycles, setCycles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);
  const [openViewDialog, setOpenViewDialog] = useState(false);
  const [selectedCycle, setSelectedCycle] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });
  
  // Form state
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    period_start: null,
    period_end: null,
    self_appraisal_start: null,
    self_appraisal_end: null,
    manager_review_start: null,
    manager_review_end: null,
  });

  // Get company_id from token
  const getCompanyId = () => {
    const token = localStorage.getItem("token");
    if (!token) return null;
    try {
      const payload = JSON.parse(atob(token.split(".")[1]));
      return payload.company_id;
    } catch (error) {
      return null;
    }
  };

  // Fetch all cycles
  const fetchCycles = async () => {
    try {
      setLoading(true);
      const company_id = getCompanyId();
      if (!company_id) {
        showSnackbar("Company ID not found in token", "error");
        return;
      }
      const token = localStorage.getItem('token');
      const response = await api.get(`/api/appraisal/cycles`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      setCycles(response.data || []);
    } catch (error) {
      showSnackbar(`Failed to load cycles: ${error.response?.data?.message || error.message}`, "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCycles();
  }, []);

  const showSnackbar = (message, severity = "success") => {
    setSnackbar({ open: true, message, severity });
  };

  // Open create dialog
  const handleCreateClick = () => {
    setFormData({
      name: "",
      description: "",
      period_start: null,
      period_end: null,
      self_appraisal_start: null,
      self_appraisal_end: null,
      manager_review_start: null,
      manager_review_end: null,
    });
    setSelectedCycle(null);
    setOpenDialog(true);
  };

  // Open edit dialog
  const handleEditClick = (cycle) => {
    setSelectedCycle(cycle);
    setFormData({
      name: cycle.name,
      description: cycle.description || "",
      period_start: new Date(cycle.period_start),
      period_end: new Date(cycle.period_end),
      self_appraisal_start: new Date(cycle.self_appraisal_start),
      self_appraisal_end: new Date(cycle.self_appraisal_end),
      manager_review_start: cycle.manager_review_start ? new Date(cycle.manager_review_start) : null,
      manager_review_end: cycle.manager_review_end ? new Date(cycle.manager_review_end) : null,
    });
    setOpenDialog(true);
  };

  // Open view dialog
  const handleViewClick = (cycle) => {
    setSelectedCycle(cycle);
    setOpenViewDialog(true);
  };

  // Handle form submit
  const handleSubmit = async () => {
    try {
      const company_id = getCompanyId();
      if (!company_id) {
        showSnackbar("Company ID not found", "error");
        return;
      }

      // Format dates
      const payload = {
        ...formData,
        period_start: formData.period_start ? formData.period_start.toISOString().split('T')[0] : null,
        period_end: formData.period_end ? formData.period_end.toISOString().split('T')[0] : null,
        self_appraisal_start: formData.self_appraisal_start ? formData.self_appraisal_start.toISOString().split('T')[0] : null,
        self_appraisal_end: formData.self_appraisal_end ? formData.self_appraisal_end.toISOString().split('T')[0] : null,
        manager_review_start: formData.manager_review_start ? formData.manager_review_start.toISOString().split('T')[0] : null,
        manager_review_end: formData.manager_review_end ? formData.manager_review_end.toISOString().split('T')[0] : null,
      };
      const token = localStorage.getItem('token');
      if (selectedCycle) {
        // Update existing
        await api.put(`/api/appraisal/cycles/${selectedCycle.id}`, payload,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );
        showSnackbar("Appraisal cycle updated successfully", "success");
      } else {
        // Create new
        await api.post(`/api/appraisal/cycles/create`, payload,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );
        showSnackbar("Appraisal cycle created successfully", "success");
      }

      setOpenDialog(false);
      fetchCycles();
    } catch (error) {
      showSnackbar(`Failed: ${error.response?.data?.message || error.message}`, "error");
    }
  };

  // Activate cycle
  const handleActivate = async (cycleId) => {
    try {
      const token = localStorage.getItem('token');
  
      await api.put(
        `/api/appraisal/cycles/${cycleId}/activate`,
        {}, // request body
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json"
          }
        }
      );
  
      showSnackbar("Appraisal window opened successfully", "success");
  
      fetchCycles();
  
    } catch (error) {
  
      showSnackbar(
        `Failed: ${error.response?.data?.message || error.message}`,
        "error"
      );
    }
  };

  // Close cycle
  const handleClose = async (cycleId) => {
    try {
      const token = localStorage.getItem('token');
      const company_id = getCompanyId();
      await api.put(`/api/appraisal/cycles/${cycleId}/close`,{},
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      showSnackbar("Appraisal window closed successfully", "success");
      fetchCycles();
    } catch (error) {
      showSnackbar(`Failed: ${error.response?.data?.message || error.message}`, "error");
    }
  };

  // Delete cycle
  const handleDelete = async (cycleId) => {
    if (!window.confirm("Are you sure you want to delete this appraisal cycle?")) return;
    
    try {
      const company_id = getCompanyId();
      const token = localStorage.getItem('token');
      await api.delete(`/api/appraisal/cycles/${cycleId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      showSnackbar("Appraisal cycle deleted successfully", "success");
      fetchCycles();
    } catch (error) {
      showSnackbar(`Failed: ${error.response?.data?.message || error.message}`, "error");
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "Draft": return "default";
      case "Active": return "success";
      case "Closed": return "error";
      case "Archived": return "warning";
      default: return "default";
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleDateString("en-IN", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
        {/* Header */}
        <Paper elevation={3} sx={{ p: 3, mb: 4 }}>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Box>
              <Typography variant="h4" gutterBottom fontWeight="bold">
                Appraisal Cycle
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Create and manage appraisal cycles. Activate cycles to open self-appraisal windows.
              </Typography>
            </Box>
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={handleCreateClick}
              sx={{ bgcolor: "#1976d2" }}
            >
              New Appraisal Cycle
            </Button>
          </Box>
        </Paper>

        {/* Cycles Table */}
        <TableContainer component={Paper} elevation={2}>
          <Table>
            <TableHead sx={{ bgcolor: "#f5f5f5" }}>
              <TableRow>
                <TableCell><strong>Cycle Name</strong></TableCell>
                <TableCell><strong>Appraisal Period</strong></TableCell>
                <TableCell><strong>Self-Appraisal Window</strong></TableCell>
                <TableCell><strong>Status</strong></TableCell>
                <TableCell><strong>Employees</strong></TableCell>
                <TableCell><strong>Submitted</strong></TableCell>
                <TableCell><strong>Actions</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 3 }}>
                    Loading appraisal cycles...
                  </TableCell>
                </TableRow>
              ) : cycles.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 3 }}>
                    No appraisal cycles found. Create your first cycle.
                  </TableCell>
                </TableRow>
              ) : (
                cycles.map((cycle) => (
                  <TableRow key={cycle.id} hover>
                    <TableCell>
                      <Typography fontWeight="medium">{cycle.name}</Typography>
                      {cycle.description && (
                        <Typography variant="caption" color="text.secondary">
                          {cycle.description.length > 50 
                            ? `${cycle.description.substring(0, 50)}...` 
                            : cycle.description}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Box>
                        <Typography variant="body2">
                          {formatDate(cycle.period_start)} - {formatDate(cycle.period_end)}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box>
                        <Typography variant="body2">
                          {formatDate(cycle.self_appraisal_start)} - {formatDate(cycle.self_appraisal_end)}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={cycle.status}
                        color={getStatusColor(cycle.status)}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Typography>{cycle.total_employees || 0}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography>
                        {cycle.submitted_count || 0} ({cycle.completion_rate || 0}%)
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Box display="flex" gap={1}>
                        <Tooltip title="View Details">
                          <IconButton size="small" onClick={() => handleViewClick(cycle)}>
                            <Visibility fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        
                        {cycle.status === "Draft" && (
                          <>
                            <Tooltip title="Edit">
                              <IconButton size="small" onClick={() => handleEditClick(cycle)}>
                                <Edit fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Activate (Open Window)">
                              <IconButton 
                                size="small" 
                                color="success"
                                onClick={() => handleActivate(cycle.id)}
                              >
                                <PlayCircleOutline fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Delete">
                              <IconButton 
                                size="small" 
                                color="error"
                                onClick={() => handleDelete(cycle.id)}
                              >
                                <Delete fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </>
                        )}
                        
                        {cycle.status === "Active" && (
                          <Tooltip title="Close Window">
                            <IconButton 
                              size="small" 
                              color="warning"
                              onClick={() => handleClose(cycle.id)}
                            >
                              <StopCircle fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                      </Box>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Create/Edit Dialog */}
        <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="md" fullWidth>
          <DialogTitle>
            {selectedCycle ? "Edit Appraisal Cycle" : "Create New Appraisal Cycle"}
          </DialogTitle>
          <DialogContent>
            <Box sx={{ pt: 2 }}>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Cycle Name *"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    required
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    multiline
                    rows={2}
                    label="Description"
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  />
                </Grid>
                
                {/* Appraisal Period */}
                <Grid item xs={12}>
                  <Typography variant="subtitle2" gutterBottom color="primary">
                    Appraisal Period (Period being evaluated)
                  </Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <DatePicker
                    label="Period Start *"
                    value={formData.period_start}
                    onChange={(date) => setFormData({ ...formData, period_start: date })}
                    renderInput={(params) => <TextField {...params} fullWidth required />}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <DatePicker
                    label="Period End *"
                    value={formData.period_end}
                    onChange={(date) => setFormData({ ...formData, period_end: date })}
                    renderInput={(params) => <TextField {...params} fullWidth required />}
                  />
                </Grid>
                
                {/* Self-Appraisal Window */}
                <Grid item xs={12}>
                  <Typography variant="subtitle2" gutterBottom color="primary">
                    Self-Appraisal Window (When employees can submit)
                  </Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <DatePicker
                    label="Window Start *"
                    value={formData.self_appraisal_start}
                    onChange={(date) => setFormData({ ...formData, self_appraisal_start: date })}
                    renderInput={(params) => <TextField {...params} fullWidth required />}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <DatePicker
                    label="Window End *"
                    value={formData.self_appraisal_end}
                    onChange={(date) => setFormData({ ...formData, self_appraisal_end: date })}
                    renderInput={(params) => <TextField {...params} fullWidth required />}
                  />
                </Grid>
                
                {/* Manager Review Window (Optional) */}
                <Grid item xs={12}>
                  <Typography variant="subtitle2" gutterBottom color="text.secondary">
                    Manager Review Window 
                  </Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <DatePicker
                    label="Manager Review Start"
                    value={formData.manager_review_start}
                    onChange={(date) => setFormData({ ...formData, manager_review_start: date })}
                    renderInput={(params) => <TextField {...params} fullWidth />}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <DatePicker
                    label="Manager Review End"
                    value={formData.manager_review_end}
                    onChange={(date) => setFormData({ ...formData, manager_review_end: date })}
                    renderInput={(params) => <TextField {...params} fullWidth />}
                  />
                </Grid>
              </Grid>
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpenDialog(false)}>Cancel</Button>
            <Button 
              onClick={handleSubmit} 
              variant="contained" 
              disabled={!formData.name || !formData.period_start || !formData.period_end || 
                       !formData.self_appraisal_start || !formData.self_appraisal_end}
            >
              {selectedCycle ? "Update" : "Create"}
            </Button>
          </DialogActions>
        </Dialog>

        {/* View Dialog */}
        <Dialog open={openViewDialog} onClose={() => setOpenViewDialog(false)} maxWidth="md" fullWidth>
          {selectedCycle && (
            <>
              <DialogTitle>
                Appraisal Cycle Details
                <Chip 
                  label={selectedCycle.status} 
                  color={getStatusColor(selectedCycle.status)}
                  size="small"
                  sx={{ ml: 2 }}
                />
              </DialogTitle>
              <DialogContent>
                <Box sx={{ pt: 2 }}>
                  <Card variant="outlined" sx={{ mb: 3 }}>
                    <CardContent>
                      <Typography variant="h6" gutterBottom color="primary">
                        {selectedCycle.name}
                      </Typography>
                      {selectedCycle.description && (
                        <Typography variant="body2" paragraph>
                          {selectedCycle.description}
                        </Typography>
                      )}
                      <Typography variant="caption" color="text.secondary">
                        Created by: {selectedCycle.created_by} • Created: {formatDate(selectedCycle.createdAt)}
                      </Typography>
                    </CardContent>
                  </Card>
                  
                  <Grid container spacing={3}>
                    <Grid item xs={12} md={6}>
                      <Card variant="outlined">
                        <CardContent>
                          <Typography variant="subtitle1" gutterBottom fontWeight="bold" color="primary">
                            <DateRange sx={{ verticalAlign: "middle", mr: 1 }} />
                            Appraisal Period
                          </Typography>
                          <Box sx={{ pl: 3 }}>
                            <Typography variant="body2">
                              <strong>Start:</strong> {formatDate(selectedCycle.period_start)}
                            </Typography>
                            <Typography variant="body2">
                              <strong>End:</strong> {formatDate(selectedCycle.period_end)}
                            </Typography>
                          </Box>
                        </CardContent>
                      </Card>
                    </Grid>
                    
                    <Grid item xs={12} md={6}>
                      <Card variant="outlined">
                        <CardContent>
                          <Typography variant="subtitle1" gutterBottom fontWeight="bold" color="primary">
                            <AccessTime sx={{ verticalAlign: "middle", mr: 1 }} />
                            Self-Appraisal Window
                          </Typography>
                          <Box sx={{ pl: 3 }}>
                            <Typography variant="body2">
                              <strong>Opens:</strong> {formatDate(selectedCycle.self_appraisal_start)}
                            </Typography>
                            <Typography variant="body2">
                              <strong>Closes:</strong> {formatDate(selectedCycle.self_appraisal_end)}
                            </Typography>
                          </Box>
                        </CardContent>
                      </Card>
                    </Grid>
                    
                    {selectedCycle.manager_review_start && (
                      <Grid item xs={12}>
                        <Card variant="outlined">
                          <CardContent>
                            <Typography variant="subtitle1" gutterBottom fontWeight="bold">
                              Manager Review Window
                            </Typography>
                            <Box sx={{ pl: 3 }}>
                              <Typography variant="body2">
                                <strong>Start:</strong> {formatDate(selectedCycle.manager_review_start)}
                              </Typography>
                              <Typography variant="body2">
                                <strong>End:</strong> {formatDate(selectedCycle.manager_review_end)}
                              </Typography>
                            </Box>
                          </CardContent>
                        </Card>
                      </Grid>
                    )}
                    
                    <Grid item xs={12}>
                      <Card variant="outlined">
                        <CardContent>
                          <Typography variant="subtitle1" gutterBottom fontWeight="bold">
                            Statistics
                          </Typography>
                          <Grid container spacing={2}>
                            <Grid item xs={6} md={3}>
                              <Typography variant="body2" color="text.secondary">Total Employees</Typography>
                              <Typography variant="h6">{selectedCycle.total_employees || 0}</Typography>
                            </Grid>
                            <Grid item xs={6} md={3}>
                              <Typography variant="body2" color="text.secondary">Submitted</Typography>
                              <Typography variant="h6">{selectedCycle.submitted_count || 0}</Typography>
                            </Grid>
                            <Grid item xs={6} md={3}>
                              <Typography variant="body2" color="text.secondary">Completion Rate</Typography>
                              <Typography variant="h6">{selectedCycle.completion_rate || 0}%</Typography>
                            </Grid>
                            <Grid item xs={6} md={3}>
                              <Typography variant="body2" color="text.secondary">Status</Typography>
                              <Chip 
                                label={selectedCycle.status} 
                                color={getStatusColor(selectedCycle.status)}
                                size="small"
                              />
                            </Grid>
                          </Grid>
                        </CardContent>
                      </Card>
                    </Grid>
                  </Grid>
                </Box>
              </DialogContent>
              <DialogActions>
                <Button onClick={() => setOpenViewDialog(false)}>Close</Button>
              </DialogActions>
            </>
          )}
        </Dialog>

        {/* Snackbar */}
        <Snackbar
          open={snackbar.open}
          autoHideDuration={6000}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
        >
          <Alert 
            onClose={() => setSnackbar({ ...snackbar, open: false })} 
            severity={snackbar.severity}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Container>
    </LocalizationProvider>
  );
};

export default AppraisalCycleManager;